import { useState, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { LockClosedIcon } from '@heroicons/react/24/solid';

interface UpgradeModalData {
  message: string;
  feature?: string;
  requiredPlan?: string;
}

export default function UpgradeModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [modalData, setModalData] = useState<UpgradeModalData | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleUpgradeRequired = (event: CustomEvent) => {
      setModalData(event.detail);
      setIsOpen(true);
    };

    window.addEventListener('upgrade-required', handleUpgradeRequired as EventListener);

    return () => {
      window.removeEventListener('upgrade-required', handleUpgradeRequired as EventListener);
    };
  }, []);

  const handleUpgrade = () => {
    setIsOpen(false);
    navigate('/pricing');
  };

  const closeModal = () => {
    setIsOpen(false);
    setModalData(null);
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={closeModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    Upgrade Required
                  </Dialog.Title>
                  <button
                    onClick={closeModal}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="mt-2">
                  <div className="flex justify-center mb-4">
                    <div className="bg-blue-100 rounded-full p-3">
                      <LockClosedIcon className="h-8 w-8 text-blue-600" />
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 text-center">
                    {modalData?.message || 'This feature requires an upgraded plan.'}
                  </p>

                  {modalData?.requiredPlan && (
                    <p className="mt-2 text-sm text-gray-900 text-center font-medium">
                      Required plan: {modalData.requiredPlan}
                    </p>
                  )}
                </div>

                <div className="mt-6 flex gap-3">
                  <button
                    type="button"
                    className="flex-1 inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    onClick={handleUpgrade}
                  >
                    View Plans
                  </button>
                  <button
                    type="button"
                    className="flex-1 inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    onClick={closeModal}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}